/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';

import { RegistrationFromInvite as EmailOrGoogleSSOFrom } from '@hotjar/webapp/features/Registration';
import { getInvitationByToken } from '@hotjar/webapp/domains/invitations/api/invitations';
import { checkEmail } from '@hotjar/webapp/domains/check-email/api/check-email';
import { setPageTitle } from '@hotjar/webapp/utils/document';
import { restDefaultHandlers } from '@hotjar/webapp/utils/rest-default-handlers';
import { logoutIfAlreadyIdentified } from '@hotjar/tracking';
import { useGoogleSsoSignUp } from '@hotjar/webapp/features/Registration/hooks/useGoogleSsoSignUp';
import type { RegistrationDto } from '@hotjar/webapp/domains/acquisition/register/dto';
import { useCurrentUser } from '@hotjar/user';
import { SAMLSSOForm } from '@hotjar/webapp/features/JoinTeam/SAMLSSOForm';
import type { IdentityProviderDetails } from '@hotjar/webapp/features/JoinTeam/SAMLSSOForm';
import { useAcceptInvite } from '@hotjar/webapp/features/JoinTeam/useAcceptInvite';
import { useJoinTeamQueryParameters } from '@hotjar/webapp/features/JoinTeam/useJoinTeamQueryParameters';

export const JoinTeam = (): JSX.Element => {
  const [ssoErrorVisible, setSsoErrorVisible] = useState(false);

  const {
    tokenQueryParam,
    nextParam,
    errorCodeQueryParam,
    utmSourceQueryParam,
    utmMediumQueryParam,
    utmCampaignQueryParam,
  } = useJoinTeamQueryParameters();

  const [isSAMLSSOStep, setIsSAMLSSOStep] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSSOError, setIsSSOError] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isGoogleSSOProvider, setIsGoogleSSOProvider] = useState(false);
  const [ssoToken, setSSOToken] = useState('');
  const [identityProviderDetails, setIdentityProviderDetails] = useState<IdentityProviderDetails>();
  const [authenticatedUserName, setAuthenticatedUserName] = useState('');

  const user = useCurrentUser();

  useEffect(() => {
    setAuthenticatedUserName(user?.data?.name || '');
  }, [user?.data?.name]);

  // const { t } = useTranslation();
  const { registerData: googleSsoMessageData, authenticate } = useGoogleSsoSignUp(true);
  const { acceptInvite } = useAcceptInvite({ email, ssoToken, isGoogleSSOProvider });

  const onGoogleSsoMessageReceived = ({
    name: googleSSOName,
    email: googleSSOEmail,
    sso_token,
  }: RegistrationDto) => {
    if (googleSSOEmail !== email) {
      setSsoErrorVisible(true);
      return;
    }
    setEmail(googleSSOEmail);
    setName(googleSSOName);
    setIsGoogleSSOProvider(true);
    setSSOToken(sso_token);
  };

  useEffect(() => {
    if (!googleSsoMessageData) return;
    onGoogleSsoMessageReceived(googleSsoMessageData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleSsoMessageData]);

  const isAuthenticatedWithGoogle = (): boolean => !!(email && isGoogleSSOProvider);

  async function init(token: string): Promise<void> {
    try {
      const { success, invitation } = await getInvitationByToken(token);
      setIsLoaded(true);

      if (!success || !invitation) {
        setEmailExists(true);
        setTokenInvalid(true);
        return;
      }
      const {
        email: invitationEmail,
        organizationName: invitationOrganizationName,
        identityProvider,
      } = invitation;

      setEmailExists(!identityProvider);
      setIdentityProviderDetails(identityProvider);
      setEmail(invitationEmail);
      setOrganizationName(invitationOrganizationName);

      if (identityProvider) {
        // Use static text due to #_incident-627
        // setPageTitle(t('common:activation.saml-sso.signin.sso'));
        setPageTitle('SAML single sign-on');
        logoutIfAlreadyIdentified();
        setIsSAMLSSOStep(true);
        return;
      }

      const { exists } = await checkEmail(email);
      setEmailExists(exists);

      if (!exists) {
        logoutIfAlreadyIdentified();
      }
    } catch (err) {
      restDefaultHandlers.onError(
        err as { status: number; data?: Record<string, string | boolean> },
      );
    }
  }

  useEffect(() => {
    if (tokenQueryParam) {
      init(tokenQueryParam);
    } else {
      setTokenInvalid(!errorCodeQueryParam);
      setIsLoaded(true);
      setIsSSOError(!!errorCodeQueryParam);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGoogleBtnClick = () => {
    authenticate({
      next: nextParam || '',
      utmSource: utmSourceQueryParam || '',
      utmMedium: utmMediumQueryParam || '',
      utmCampaign: utmCampaignQueryParam || '',
    });
  };

  return isSAMLSSOStep ? (
    <SAMLSSOForm
      hasLoaded={isLoaded}
      identityProviderDetails={identityProviderDetails}
      email={email}
      organizationName={organizationName}
    />
  ) : (
    <EmailOrGoogleSSOFrom
      isLoading={!isLoaded}
      isSsoError={isSSOError}
      isInvalidToken={tokenInvalid}
      emailExists={emailExists}
      loggedInAs={authenticatedUserName}
      organizationName={organizationName}
      email={email}
      name={name}
      isAuthenticatedWithGoogle={isAuthenticatedWithGoogle()}
      isGoogleSsoError={ssoErrorVisible}
      onGoogleBtnClick={onGoogleBtnClick}
      onSubmitBtnClick={acceptInvite}
    />
  );
};
